import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  InputAdornment,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getDevtoolsAuthToken } from "../../devtools/utils/devtoolsAuth";

const emptyDraft = {
  slug: "",
  title: "",
  description: "",
  content: "",
  versions: [],
  module_names: [],
};

function buildHeaders(includeJson = false) {
  const token = getDevtoolsAuthToken();
  const headers = {};
  if (includeJson) {
    headers["Content-Type"] = "application/json";
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}


function tokenizeWords(text = "") {
  return String(text).match(/\s+|[^\s]+/g) || [];
}

function buildWordDiffSegments(beforeText = "", afterText = "") {
  const beforeTokens = tokenizeWords(beforeText);
  const afterTokens = tokenizeWords(afterText);
  const maxLen = Math.max(beforeTokens.length, afterTokens.length);
  const before = [];
  const after = [];

  for (let index = 0; index < maxLen; index += 1) {
    const oldToken = beforeTokens[index];
    const newToken = afterTokens[index];

    if (oldToken === newToken) {
      if (oldToken !== undefined) {
        before.push({ text: oldToken, variant: "same" });
        after.push({ text: newToken, variant: "same" });
      }
      continue;
    }

    if (oldToken !== undefined) {
      before.push({ text: oldToken, variant: "removed" });
    }
    if (newToken !== undefined) {
      after.push({ text: newToken, variant: "added" });
    }
  }

  return { before, after };
}

function renderDiffSegments(segments = [], side = "after") {
  if (!segments.length) {
    return <Typography component="span" variant="body2" color="text.secondary">—</Typography>;
  }

  return segments.map((segment, index) => {
    const isChanged = segment.variant === "added" || segment.variant === "removed";
    return (
      <Box
        key={`${side}-${index}-${segment.text}`}
        component="span"
        sx={{
          display: "inline",
          borderRadius: 0.5,
          px: isChanged ? 0.25 : 0,
          py: isChanged ? 0.1 : 0,
          backgroundColor:
            segment.variant === "added"
              ? "success.light"
              : segment.variant === "removed"
                ? "error.light"
                : "transparent",
          textDecoration: segment.variant === "removed" ? "line-through" : "none",
        }}
      >
        {segment.text}
      </Box>
    );
  });
}

function TreeNode({ node, depth = 0, isMobile = false, defaultExpanded = depth < 1, onCopyPath = null }) {
  const isDirectory = node?.type === "directory";
  const [expanded, setExpanded] = useState(defaultExpanded);
  const children = Array.isArray(node?.children) ? node.children : [];
  const warnings = Array.isArray(node?.warnings) ? node.warnings : [];
  const displayPath = node?.path || node?.relative_path || node?.name || "";

  return (
    <Box sx={{ pl: depth * (isMobile ? 1 : 1.5), py: 0.25 }}>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
        {isDirectory ? (
          <Button size="small" variant="text" onClick={() => setExpanded((value) => !value)} sx={{ minWidth: 28, px: 0.5 }}>
            {expanded ? "▾" : "▸"}
          </Button>
        ) : (
          <Box sx={{ width: 28, flexShrink: 0 }} />
        )}
        <Typography
          variant="body2"
          color={isDirectory ? "text.primary" : "text.secondary"}
          sx={{ wordBreak: "break-word", flex: 1, minWidth: 0 }}
        >
          {isDirectory ? "📁" : "📄"} {node?.name}
        </Typography>
        {warnings.map((warning) => (
          <Chip key={`${node?.name}-${warning}`} size="small" color="warning" label={warning} />
        ))}
        {displayPath && onCopyPath ? (
          <Button size="small" variant="text" onClick={() => onCopyPath(displayPath)}>
            Copy path
          </Button>
        ) : null}
      </Stack>
      {isDirectory && expanded && children.length
        ? children.map((child) => (
            <TreeNode
              key={`${node?.path || node?.name}-${child.name}-${child.path || child.relative_path || ""}`}
              node={child}
              depth={depth + 1}
              isMobile={isMobile}
              defaultExpanded={false}
              onCopyPath={onCopyPath}
            />
          ))
        : null}
    </Box>
  );
}

function escapeHtml(value = "") {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function inlineMarkdownToHtml(text = "") {
  let html = escapeHtml(text);
  html = html.replace(/`([^`]+)`/g, "<code>$1</code>");
  html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/__([^_]+)__/g, "<strong>$1</strong>");
  html = html.replace(/(^|\W)\*([^*]+)\*(?=\W|$)/g, "$1<em>$2</em>");
  html = html.replace(/(^|\W)_([^_]+)_(?=\W|$)/g, "$1<em>$2</em>");
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>');
  return html;
}

function MarkdownPreview({ content }) {
  const lines = (content || "").replace(/\r\n/g, "\n").split("\n");
  const nodes = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      i += 1;
      continue;
    }

    if (trimmed.startsWith("```")) {
      const codeLines = [];
      i += 1;
      while (i < lines.length && !lines[i].trim().startsWith("```")) {
        codeLines.push(lines[i]);
        i += 1;
      }
      if (i < lines.length) {
        i += 1;
      }
      nodes.push(
        <Box
          key={`node-${key++}`}
          component="pre"
          sx={{
            backgroundColor: "grey.100",
            borderRadius: 1,
            p: 1.5,
            overflow: "auto",
            fontFamily: "monospace",
            fontSize: 13,
            m: 0,
            mb: 1.5,
          }}
        >
          {codeLines.join("\n")}
        </Box>
      );
      continue;
    }

    if (/^---+$/.test(trimmed) || /^\*\*\*+$/.test(trimmed)) {
      nodes.push(<Divider key={`node-${key++}`} sx={{ my: 2 }} />);
      i += 1;
      continue;
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const text = headingMatch[2];
      const variantMap = {
        1: "h4",
        2: "h5",
        3: "h6",
        4: "subtitle1",
        5: "subtitle2",
        6: "body1",
      };
      nodes.push(
        <Typography
          key={`node-${key++}`}
          variant={variantMap[level] || "body1"}
          sx={{ mt: level <= 2 ? 2 : 1.5, mb: 1, fontWeight: 700 }}
          dangerouslySetInnerHTML={{ __html: inlineMarkdownToHtml(text) }}
        />
      );
      i += 1;
      continue;
    }

    if (/^>\s+/.test(trimmed)) {
      const quoteLines = [];
      while (i < lines.length && /^>\s+/.test(lines[i].trim())) {
        quoteLines.push(lines[i].trim().replace(/^>\s+/, ""));
        i += 1;
      }
      nodes.push(
        <Box key={`node-${key++}`} sx={{ borderLeft: 3, borderColor: "divider", pl: 1.5, py: 0.5, mb: 1.5 }}>
          {quoteLines.map((quoteLine, index) => (
            <Typography
              key={`quote-${index}`}
              variant="body2"
              color="text.secondary"
              dangerouslySetInnerHTML={{ __html: inlineMarkdownToHtml(quoteLine) }}
            />
          ))}
        </Box>
      );
      continue;
    }

    if (/^(-|\*)\s+/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^\s*(-|\*)\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*(-|\*)\s+/, ""));
        i += 1;
      }
      nodes.push(
        <Box key={`node-${key++}`} component="ul" sx={{ mt: 0.5, mb: 1.5, pl: 3 }}>
          {items.map((item, index) => (
            <li key={`li-${index}`}>
              <Typography variant="body2" dangerouslySetInnerHTML={{ __html: inlineMarkdownToHtml(item) }} />
            </li>
          ))}
        </Box>
      );
      continue;
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ""));
        i += 1;
      }
      nodes.push(
        <Box key={`node-${key++}`} component="ol" sx={{ mt: 0.5, mb: 1.5, pl: 3 }}>
          {items.map((item, index) => (
            <li key={`ol-${index}`}>
              <Typography variant="body2" dangerouslySetInnerHTML={{ __html: inlineMarkdownToHtml(item) }} />
            </li>
          ))}
        </Box>
      );
      continue;
    }

    const paragraphLines = [trimmed];
    i += 1;
    while (
      i < lines.length &&
      lines[i].trim() &&
      !lines[i].trim().startsWith("#") &&
      !lines[i].trim().startsWith(">") &&
      !lines[i].trim().startsWith("```") &&
      !/^\s*(-|\*)\s+/.test(lines[i]) &&
      !/^\s*\d+\.\s+/.test(lines[i]) &&
      !/^---+$/.test(lines[i].trim()) &&
      !/^\*\*\*+$/.test(lines[i].trim())
    ) {
      paragraphLines.push(lines[i].trim());
      i += 1;
    }

    nodes.push(
      <Typography
        key={`node-${key++}`}
        variant="body2"
        sx={{ mb: 1.5, whiteSpace: "normal", lineHeight: 1.7 }}
        dangerouslySetInnerHTML={{ __html: inlineMarkdownToHtml(paragraphLines.join(" ")) }}
      />
    );
  }

  if (!nodes.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Nothing to preview yet.
      </Typography>
    );
  }

  return <Fragment>{nodes}</Fragment>;
}

function DiffPreview({ diff }) {
  if (!diff?.has_changes) {
    return (
      <Typography variant="body2" color="text.secondary">
        No content changes detected.
      </Typography>
    );
  }

  const lineItems = diff.lines || [];

  return (
    <Stack spacing={1.25}>
      <Paper variant="outlined" sx={{ p: 1.25, backgroundColor: "grey.50" }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap flexWrap="wrap">
          <Chip size="small" label={`Before: ${diff.line_count_before || 0} lines`} />
          <Chip size="small" label={`After: ${diff.line_count_after || 0} lines`} />
          <Chip size="small" color="success" label={`Added: ${diff.added_lines || 0}`} />
          <Chip size="small" color="error" label={`Removed: ${diff.removed_lines || 0}`} />
        </Stack>
      </Paper>

      <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            backgroundColor: "grey.100",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Box sx={{ px: 1.5, py: 1, borderRight: { md: 1 }, borderColor: "divider" }}>
            <Typography variant="subtitle2">Before</Typography>
          </Box>
          <Box sx={{ px: 1.5, py: 1 }}>
            <Typography variant="subtitle2">After</Typography>
          </Box>
        </Box>

        <Box sx={{ maxHeight: 380, overflow: "auto" }}>
          {lineItems.map((line, index) => {
            if (line.type === "meta") {
              return (
                <Box
                  key={`diff-meta-${index}`}
                  sx={{ px: 1.5, py: 0.75, backgroundColor: "grey.100", borderBottom: 1, borderColor: "divider" }}
                >
                  <Typography variant="caption" sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}>
                    {line.text || " "}
                  </Typography>
                </Box>
              );
            }

            if (line.type === "changed_pair") {
              const segments = buildWordDiffSegments(line.before || "", line.after || "");
              return (
                <Box
                  key={`diff-pair-${index}`}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  <Box sx={{ px: 1.5, py: 1, borderRight: { md: 1 }, borderColor: "divider", backgroundColor: "rgba(211, 47, 47, 0.08)" }}>
                    <Typography variant="body2" sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {renderDiffSegments(segments.before, "before")}
                    </Typography>
                  </Box>
                  <Box sx={{ px: 1.5, py: 1, backgroundColor: "rgba(46, 125, 50, 0.08)" }}>
                    <Typography variant="body2" sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {renderDiffSegments(segments.after, "after")}
                    </Typography>
                  </Box>
                </Box>
              );
            }

            const isAdded = line.type === "added";
            const isRemoved = line.type === "removed";
            return (
              <Box
                key={`diff-line-${index}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    borderRight: { md: 1 },
                    borderColor: "divider",
                    backgroundColor: isRemoved ? "rgba(211, 47, 47, 0.08)" : "transparent",
                  }}
                >
                  <Typography variant="body2" sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {isAdded ? "" : (line.text || " " )}
                  </Typography>
                </Box>
                <Box sx={{ px: 1.5, py: 0.75, backgroundColor: isAdded ? "rgba(46, 125, 50, 0.08)" : "transparent" }}>
                  <Typography variant="body2" sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {isRemoved ? "" : (line.text || " " )}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Stack>
  );
}

export default function DocumentManagerPanel({
  selectedProjectId: controlledProjectId = "",
  projectOptions = null,
  projectError: externalProjectError = "",
  projectsLoading: externalProjectsLoading = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [documents, setDocuments] = useState([]);
  const [selectedDocumentSummary, setSelectedDocumentSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedSlug, setSelectedSlug] = useState("");
  const [draft, setDraft] = useState(emptyDraft);
  const [baselineDraft, setBaselineDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [versionPreview, setVersionPreview] = useState("");
  const [versionLoading, setVersionLoading] = useState(false);
  const [tree, setTree] = useState([]);
  const [storageStatus, setStorageStatus] = useState(null);
  const [treeModalOpen, setTreeModalOpen] = useState(false);
  const [copiedPath, setCopiedPath] = useState("");
  const [initializing, setInitializing] = useState(false);
  const [previewTab, setPreviewTab] = useState("preview");
  const [versionPreviewTab, setVersionPreviewTab] = useState("preview");
  const [reviewOpen, setReviewOpen] = useState(false);
  const [diffLoading, setDiffLoading] = useState(false);
  const [saveDiff, setSaveDiff] = useState(null);
  const [availableModules, setAvailableModules] = useState([]);
  const [moduleFilter, setModuleFilter] = useState(["__ALL_DOCUMENTS__"]);
  const [listMode, setListMode] = useState("explorer");
  const [mobileSection, setMobileSection] = useState("documents");
  const [documentSearch, setDocumentSearch] = useState("");
  const [expandedGroups, setExpandedGroups] = useState({});
  const [documentStandards, setDocumentStandards] = useState(null);
  const [complianceSummary, setComplianceSummary] = useState(null);
  const [creatingStandardDoc, setCreatingStandardDoc] = useState("");
  const [projects, setProjects] = useState(Array.isArray(projectOptions) ? projectOptions : []);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectError, setProjectError] = useState("");
  const [localProjectId, setLocalProjectId] = useState("");

  const effectiveProjectId = String(controlledProjectId || localProjectId || "").trim();
  const usingExternalProjectControl = controlledProjectId !== "" || Array.isArray(projectOptions);
  const effectiveProjects = Array.isArray(projectOptions) ? projectOptions : projects;
  const effectiveProjectsLoading = usingExternalProjectControl ? externalProjectsLoading : projectsLoading;
  const effectiveProjectError = usingExternalProjectControl ? externalProjectError : projectError;

  const buildDocsUrl = (path, query = {}) => {
    const params = new URLSearchParams();
    Object.entries(query || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") return;
      params.set(key, value);
    });
    if (effectiveProjectId) {
      params.set("project_id", effectiveProjectId);
    }
    return `${path}${params.toString() ? `?${params.toString()}` : ""}`;
  };

  const withProjectPayload = (payload = {}) => ({
    ...payload,
    project_id: effectiveProjectId || null,
  });


  const modulePickerOptions = useMemo(() => {
    const counts = new Map();
    let unassignedCount = 0;
    (documents || []).forEach((doc) => {
      const docModules = Array.isArray(doc.module_names) ? doc.module_names : [];
      if (!docModules.length) {
        unassignedCount += 1;
      }
      docModules.forEach((moduleName) => {
        counts.set(moduleName, (counts.get(moduleName) || 0) + 1);
      });
    });
    return [
      { value: "__ALL_DOCUMENTS__", label: `All documents (${documents.length})` },
      { value: "__NO_MODULE__", label: `Non-module documents (${unassignedCount})` },
      ...availableModules.map((moduleName) => ({
        value: moduleName,
        label: `${moduleName} (${counts.get(moduleName) || 0})`,
      })),
    ];
  }, [availableModules, documents]);

  const normalizedModuleFilter = useMemo(() => {
    const rawValues = Array.isArray(moduleFilter) ? moduleFilter : [moduleFilter];
    const cleaned = Array.from(new Set(rawValues.filter(Boolean)));
    if (!cleaned.length || cleaned.includes("__ALL_DOCUMENTS__")) {
      return ["__ALL_DOCUMENTS__"];
    }
    const withoutSpecial = cleaned.filter((value) => value !== "__ALL_DOCUMENTS__");
    if (withoutSpecial.includes("__NO_MODULE__") && withoutSpecial.length > 1) {
      return withoutSpecial.filter((value) => value !== "__NO_MODULE__");
    }
    return withoutSpecial.length ? withoutSpecial : ["__ALL_DOCUMENTS__"];
  }, [moduleFilter]);

  const activeFilterLabel = useMemo(() => {
    if (normalizedModuleFilter.includes("__ALL_DOCUMENTS__")) return `All documents (${documents.length})`;
    if (normalizedModuleFilter.length === 1 && normalizedModuleFilter[0] === "__NO_MODULE__") {
      return modulePickerOptions.find((option) => option.value === "__NO_MODULE__")?.label || "Non-module documents";
    }
    return `${normalizedModuleFilter.length} module scope${normalizedModuleFilter.length === 1 ? "" : "s"} selected`;
  }, [documents.length, modulePickerOptions, normalizedModuleFilter]);

  const filteredDocuments = useMemo(() => {
    if (normalizedModuleFilter.includes("__ALL_DOCUMENTS__")) return documents;
    return documents.filter((doc) => {
      const docModules = Array.isArray(doc.module_names) ? doc.module_names : [];
      if (normalizedModuleFilter.includes("__NO_MODULE__") && !docModules.length) {
        return true;
      }
      return normalizedModuleFilter.some((value) => docModules.includes(value));
    });
  }, [documents, normalizedModuleFilter]);

  const visibleDocumentCount = useMemo(() => {
    const searchTerm = (documentSearch || "").trim().toLowerCase();
    if (!searchTerm) return filteredDocuments.length;
    return (filteredDocuments || []).filter((doc) => {
      const haystack = [
        doc.title || "",
        doc.slug || "",
        ...(Array.isArray(doc.module_names) ? doc.module_names : []),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(searchTerm);
    }).length;
  }, [documentSearch, filteredDocuments]);


  const groupedDocuments = useMemo(() => {
    const searchTerm = (documentSearch || "").trim().toLowerCase();
    const filtered = (filteredDocuments || []).filter((doc) => {
      if (!searchTerm) return true;
      const haystack = [
        doc.title || "",
        doc.slug || "",
        ...(Array.isArray(doc.module_names) ? doc.module_names : []),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(searchTerm);
    });

    const groupsMap = new Map();
    filtered.forEach((doc) => {
      const groupName = normalizedModuleFilter.includes("__ALL_DOCUMENTS__")
        ? ((Array.isArray(doc.module_names) && doc.module_names[0]) || "Non-module documents")
        : normalizedModuleFilter.includes("__NO_MODULE__") && !(Array.isArray(doc.module_names) ? doc.module_names.length : 0)
          ? "Non-module documents"
          : ((Array.isArray(doc.module_names) && doc.module_names[0]) || normalizedModuleFilter[0] || "Documents");
      if (!groupsMap.has(groupName)) {
        groupsMap.set(groupName, []);
      }
      groupsMap.get(groupName).push(doc);
    });

    return Array.from(groupsMap.entries())
      .map(([groupName, docs]) => ({
        groupName,
        docs: [...docs].sort((a, b) => (a.title || "").localeCompare(b.title || "")),
      }))
      .sort((a, b) => a.groupName.localeCompare(b.groupName));
  }, [documentSearch, filteredDocuments, normalizedModuleFilter]);

  const recentDocuments = useMemo(() => {
    const searchTerm = (documentSearch || "").trim().toLowerCase();
    return [...(filteredDocuments || [])]
      .filter((doc) => {
        if (!searchTerm) return true;
        const haystack = [doc.title || "", doc.slug || "", ...(Array.isArray(doc.module_names) ? doc.module_names : [])]
          .join(" ")
          .toLowerCase();
        return haystack.includes(searchTerm);
      })
      .sort((a, b) => String(b.updated_at || "").localeCompare(String(a.updated_at || "")))
      .slice(0, 25);
  }, [documentSearch, filteredDocuments]);

  const visibleComplianceModules = useMemo(() => {
    const modules = (complianceSummary?.modules || []).filter((entry) => {
      if (normalizedModuleFilter.includes("__ALL_DOCUMENTS__")) return true;
      if (normalizedModuleFilter.includes("__NO_MODULE__") && entry.module_name === "__shared__") return true;
      return normalizedModuleFilter.includes(entry.module_name);
    });
    return modules.slice(0, 6);
  }, [complianceSummary, normalizedModuleFilter]);

  const hasUnsavedChanges = useMemo(() => {
    return (
      draft.title !== baselineDraft.title ||
      draft.description !== baselineDraft.description ||
      draft.content !== baselineDraft.content ||
      JSON.stringify(draft.module_names || []) !== JSON.stringify(baselineDraft.module_names || [])
    );
  }, [draft, baselineDraft]);

  const loadProjects = async () => {
    setProjectsLoading(true);
    setProjectError("");
    try {
      const res = await fetch("/api/dev-ide/projects");
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to load projects.");
      }
      const nextProjects = data.projects || [];
      setProjects(nextProjects);
      if (!controlledProjectId && !localProjectId && nextProjects.length) {
        const runningApp = nextProjects.find((item) => String(item?.project_id || "") === "running_app");
        const firstProject = runningApp || nextProjects[0];
        if (firstProject?.project_id) {
          setLocalProjectId(firstProject.project_id);
        }
      }
    } catch (err) {
      setProjectError(err.message || "Failed to load projects.");
    } finally {
      setProjectsLoading(false);
    }
  };

  const loadAvailableModules = async () => {
    try {
      const params = new URLSearchParams();
      if (effectiveProjectId) {
        params.set("project_id", effectiveProjectId);
      }
      const res = await fetch(`/api/docs/modules${params.toString() ? `?${params.toString()}` : ""}`);
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to load modules.");
      }
      const normalizedModules = (data.modules || [])
        .map((moduleEntry) => {
          if (typeof moduleEntry === "string") return moduleEntry;
          if (!moduleEntry || typeof moduleEntry !== "object") return "";
          return moduleEntry.module || moduleEntry.module_name || moduleEntry.name || moduleEntry.label || "";
        })
        .filter(Boolean);
      setAvailableModules(Array.from(new Set(normalizedModules)));
    } catch (err) {
      setError(err.message || "Failed to load modules.");
    }
  };

  const loadDocumentStandards = async (moduleNamesOverride = null) => {
    try {
      const modules = Array.isArray(moduleNamesOverride)
        ? moduleNamesOverride.filter(Boolean)
        : normalizedModuleFilter.includes("__ALL_DOCUMENTS__")
          ? []
          : normalizedModuleFilter.filter((value) => !value.startsWith("__"));
      const res = await fetch(buildDocsUrl("/api/docs/documents/standards", modules.length ? { modules: modules.join(",") } : {}), {
        headers: buildHeaders(false),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to load document standards.");
      }
      setDocumentStandards(data.standard || null);
      setComplianceSummary(data.compliance_summary || null);
    } catch (err) {
      setError(err.message || "Failed to load document standards.");
    }
  };

  const loadStorageStatus = async () => {
    try {
      const res = await fetch(buildDocsUrl("/api/docs/documents/status"), {
        headers: buildHeaders(false),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to load document storage status.");
      }
      setStorageStatus(data.status || null);
    } catch (err) {
      setError(err.message || "Failed to load document storage status.");
    }
  };

  const loadTree = async () => {
    try {
      const res = await fetch(buildDocsUrl("/api/docs/documents/tree"), {
        headers: buildHeaders(false),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to load storage tree.");
      }
      setTree(data.tree || []);
    } catch (err) {
      setError(err.message || "Failed to load storage tree.");
    }
  };

  const loadDocuments = async (preferSlug = "") => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(buildDocsUrl("/api/docs/documents"), {
        headers: buildHeaders(false),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to load documents.");
      }
      const nextDocs = data.documents || [];
      setDocuments(nextDocs);

      const nextSlug =
        preferSlug ||
        (selectedSlug && nextDocs.some((doc) => doc.slug === selectedSlug) ? selectedSlug : nextDocs[0]?.slug || "");

      if (nextSlug) {
        await loadDocument(nextSlug, nextDocs);
      } else {
        setSelectedSlug("");
        setSelectedDocumentSummary(null);
        setDraft(emptyDraft);
        setBaselineDraft(emptyDraft);
      }

      await loadStorageStatus();
      await loadTree();
      await loadDocumentStandards();
    } catch (err) {
      setError(err.message || "Failed to load documents.");
    } finally {
      setLoading(false);
    }
  };

  const loadDocument = async (slug, docsOverride = null) => {
    setSelectedSlug(slug);
    setVersionPreview("");
    try {
      const res = await fetch(buildDocsUrl(`/api/docs/documents/${encodeURIComponent(slug)}`), {
        headers: buildHeaders(false),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to load document.");
      }
      const doc = data.document || emptyDraft;
      const nextDraft = {
        slug: doc.slug || "",
        title: doc.title || "",
        description: doc.description || "",
        content: doc.content || "",
        versions: doc.versions || [],
        module_names: doc.module_names || [],
      };
      setDraft(nextDraft);
      setBaselineDraft(nextDraft);
      const docsToSearch = docsOverride || documents;
      const matchedSummary = (docsToSearch || []).find((item) => item.slug === (doc.slug || slug)) || null;
      setSelectedDocumentSummary(matchedSummary);
      if (docsOverride) {
        setDocuments(docsOverride);
      }
      if (isMobile) {
        setMobileSection("editor");
      }
    } catch (err) {
      setError(err.message || "Failed to load document.");
    }
  };

  const initializeFolders = async () => {
    setInitializing(true);
    setError("");
    try {
      const res = await fetch("/api/docs/documents/init", {
        method: "POST",
        headers: buildHeaders(true),
        body: JSON.stringify(withProjectPayload({})),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to initialize storage.");
      }
      setStorageStatus(data.storage?.status || null);
      setTree(data.storage?.tree || []);
      await loadDocuments(selectedSlug);
    } catch (err) {
      setError(err.message || "Failed to initialize storage.");
    } finally {
      setInitializing(false);
    }
  };

  const copyTreePath = async (pathValue) => {
    const value = String(pathValue || "");
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedPath(value);
    } catch (err) {
      setCopiedPath(value);
      setError("Path selected but browser clipboard access was not available. Copy it manually from the path text.");
    }
  };

  const handleCreateStandardDocument = async (moduleName, docType) => {
    if (!moduleName || !docType) return;
    const label = (documentStandards?.document_labels || {})[docType] || docType;
    const template = (documentStandards?.templates || {})[`${docType}.md`] || `# {{module_name}} - ${label}\n`;
    const content = template.replace(/\{\{module_name\}\}/g, moduleName);
    setCreatingStandardDoc(`${moduleName}:${docType}`);
    setError("");
    try {
      const res = await fetch('/api/docs/documents', {
        method: 'POST',
        headers: buildHeaders(true),
        body: JSON.stringify(withProjectPayload({
          title: `${moduleName} - ${label}`,
          description: `Created from the standard module document template for ${moduleName}.`,
          content,
          module_names: [moduleName],
        })),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to create standard document.');
      }
      await loadDocuments(data.document?.slug || '');
    } catch (err) {
      setError(err.message || 'Failed to create standard document.');
    } finally {
      setCreatingStandardDoc('');
    }
  };

  useEffect(() => {
    if (!usingExternalProjectControl) {
      loadProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usingExternalProjectControl]);

  useEffect(() => {
    loadAvailableModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveProjectId, effectiveProjects.length, effectiveProjectsLoading]);


  useEffect(() => {
    if (Array.isArray(projectOptions)) {
      setProjects(projectOptions);
    }
  }, [projectOptions]);

  useEffect(() => {
    loadDocumentStandards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedModuleFilter.join(","), documents.length]);
  useEffect(() => {
    if (controlledProjectId && controlledProjectId !== localProjectId) {
      setLocalProjectId(controlledProjectId);
    }
  }, [controlledProjectId, localProjectId]);

  useEffect(() => {
    if (!effectiveProjects.length && !effectiveProjectId && effectiveProjectsLoading) {
      return;
    }
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveProjectId, effectiveProjects.length, effectiveProjectsLoading]);


  useEffect(() => {
    if (!isMobile) {
      setMobileSection("documents");
    }
  }, [isMobile]);

  useEffect(() => {
    setModuleFilter((prev) => {
      const current = Array.isArray(prev) ? prev : [prev];
      if (!current.length || current.includes("__ALL_DOCUMENTS__")) {
        return ["__ALL_DOCUMENTS__"];
      }
      const allowed = new Set(["__ALL_DOCUMENTS__", "__NO_MODULE__", ...availableModules]);
      const next = current.filter((value) => allowed.has(value));
      return next.length ? next : ["__ALL_DOCUMENTS__"];
    });
  }, [availableModules]);

  const handleCreateNew = () => {
    setSelectedSlug("");
    setSelectedDocumentSummary(null);
    setVersionPreview("");
    const nextDraft = {
      slug: "",
      title: "New Procedure",
      description: "",
      content: "# New Procedure\n\nAdd your document content here.\n",
      versions: [],
      module_names: [],
    };
    setDraft(nextDraft);
    setBaselineDraft(emptyDraft);
    if (isMobile) {
      setMobileSection("editor");
    }
  };

  const performSave = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        title: draft.title,
        description: draft.description,
        content: draft.content,
        module_names: draft.module_names || [],
      };

      const res = await fetch(
        selectedSlug ? `/api/docs/documents/${encodeURIComponent(selectedSlug)}` : "/api/docs/documents",
        {
          method: selectedSlug ? "PUT" : "POST",
          headers: buildHeaders(true),
          body: JSON.stringify(withProjectPayload(payload)),
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to save document.");
      }
      const savedDoc = data.document;
      setReviewOpen(false);
      setSaveDiff(null);
      await loadDocuments(savedDoc.slug);
    } catch (err) {
      setError(err.message || "Failed to save document.");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenSaveReview = async () => {
    if (!draft.title.trim()) {
      setError("Title is required.");
      return;
    }

    setReviewOpen(true);
    setDiffLoading(true);
    setError("");
    try {
      const res = await fetch("/api/docs/documents/diff-preview", {
        method: "POST",
        headers: buildHeaders(true),
        body: JSON.stringify(withProjectPayload({
          old_content: baselineDraft.content || "",
          new_content: draft.content || "",
        })),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to build diff preview.");
      }
      setSaveDiff(data.diff || null);
    } catch (err) {
      setError(err.message || "Failed to build diff preview.");
      setReviewOpen(false);
    } finally {
      setDiffLoading(false);
    }
  };

  const handlePreviewVersion = async (versionName) => {
    if (!selectedSlug || !versionName) return;
    setVersionLoading(true);
    try {
      const res = await fetch(
        buildDocsUrl(`/api/docs/documents/${encodeURIComponent(selectedSlug)}/versions/${encodeURIComponent(versionName)}`),
        {
          headers: buildHeaders(false),
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to load version.");
      }
      setVersionPreview(data.version?.content || "");
      if (isMobile) {
        setMobileSection("history");
      }
    } catch (err) {
      setError(err.message || "Failed to load version.");
    } finally {
      setVersionLoading(false);
    }
  };

  const handleRestoreVersion = async (versionName) => {
    if (!selectedSlug || !versionName) return;
    setSaving(true);
    try {
      const res = await fetch(buildDocsUrl(`/api/docs/documents/${encodeURIComponent(selectedSlug)}/restore`), {
        method: "POST",
        headers: buildHeaders(true),
        body: JSON.stringify(withProjectPayload({ version_name: versionName })),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to restore version.");
      }
      await loadDocuments(selectedSlug);
      setVersionPreview("");
    } catch (err) {
      setError(err.message || "Failed to restore version.");
    } finally {
      setSaving(false);
    }
  };

  const triggerBrowserDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadMd = () => {
    const filename = `${(draft.title || "document").trim().replace(/[^a-zA-Z0-9._-]+/g, "_") || "document"}.md`;
    const blob = new Blob([draft.content || ""], { type: "text/markdown;charset=utf-8" });
    triggerBrowserDownload(blob, filename);
  };

  const handleDownloadPdf = async () => {
    try {
      const res = await fetch("/api/docs/documents/render-pdf", {
        method: "POST",
        headers: buildHeaders(true),
        body: JSON.stringify(withProjectPayload({
          title: draft.title || "Document",
          content: draft.content || "",
        })),
      });
      if (!res.ok) {
        throw new Error("Failed to generate PDF.");
      }
      const blob = await res.blob();
      const filename = `${(draft.title || "document").trim().replace(/[^a-zA-Z0-9._-]+/g, "_") || "document"}.pdf`;
      triggerBrowserDownload(blob, filename);
    } catch (err) {
      setError(err.message || "Failed to generate PDF.");
    }
  };

  const handleDownloadModuleArchive = async (archiveFormat) => {
    try {
      const res = await fetch(buildDocsUrl("/api/docs/documents/module-archive", {
        format: archiveFormat,
        modules: normalizedModuleFilter.join(",") || "__ALL_DOCUMENTS__",
      }), {
        headers: buildHeaders(false),
      });
      if (!res.ok) {
        let message = `Failed to download ${archiveFormat.toUpperCase()} archive.`;
        try {
          const data = await res.json();
          message = data.error || message;
        } catch (parseErr) {
          // ignore JSON parse errors and keep fallback message
        }
        throw new Error(message);
      }
      const blob = await res.blob();
      const fallbackBase =
        normalizedModuleFilter.includes("__ALL_DOCUMENTS__")
          ? "all_documents"
          : normalizedModuleFilter.length === 1 && normalizedModuleFilter[0] === "__NO_MODULE__"
            ? "non_module_documents"
            : `${normalizedModuleFilter.join("_").replace(/[^a-zA-Z0-9._-]+/g, "_") || "module"}_documents`;
      triggerBrowserDownload(blob, `${fallbackBase}_${archiveFormat}.zip`);
    } catch (err) {
      setError(err.message || `Failed to download ${archiveFormat.toUpperCase()} archive.`);
    }
  };

  const handleModuleFilterChange = (event) => {
    const value = typeof event.target.value === "string" ? event.target.value.split(",") : event.target.value;
    const nextValue = Array.from(new Set((value || []).filter(Boolean)));
    if (!nextValue.length || nextValue.includes("__ALL_DOCUMENTS__")) {
      setModuleFilter(["__ALL_DOCUMENTS__"]);
      return;
    }
    if (nextValue.includes("__NO_MODULE__") && nextValue.length > 1) {
      setModuleFilter(nextValue.filter((item) => item !== "__NO_MODULE__"));
      return;
    }
    setModuleFilter(nextValue);
  };

  const toggleGroup = (groupName) => {
    setExpandedGroups((prev) => ({ ...prev, [groupName]: !prev[groupName] }));
  };

  const isGroupExpanded = (groupName) => {
    if (Object.prototype.hasOwnProperty.call(expandedGroups, groupName)) {
      return !!expandedGroups[groupName];
    }
    return true;
  };

  const documentsPanel = (
    <Paper sx={{ width: { xs: "100%", md: 340 }, minWidth: 0, p: { xs: 1.5, md: 2 }, overflow: "auto" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6">Documents</Typography>
          <Typography variant="caption" color="text.secondary">
            {activeFilterLabel}
          </Typography>
        </Box>
        <Button size="small" variant="contained" onClick={handleCreateNew} fullWidth={isMobile}>
          New
        </Button>
      </Stack>

      <Stack spacing={1.25} sx={{ mb: 2 }}>
        {storageStatus && !storageStatus.exists ? (
          <Alert severity="warning">
            Documents root does not exist for this project. Create it before adding or importing documents.
          </Alert>
        ) : null}
        {storageStatus?.warnings?.length ? (
          <Alert severity="warning">
            {storageStatus.warnings.slice(0, 3).map((item) => item.message).join(" ")}
            {storageStatus.warnings.length > 3 ? ` +${storageStatus.warnings.length - 3} more.` : ""}
          </Alert>
        ) : null}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button size="small" variant="outlined" onClick={initializeFolders} disabled={initializing} fullWidth>
            {initializing ? "Creating..." : storageStatus?.exists ? "Ensure Docs Folders" : "Create Documents Folder"}
          </Button>
          <Button size="small" variant="outlined" onClick={() => setTreeModalOpen(true)} fullWidth>
            View Folder Tree
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-word" }}>
          Docs root: {storageStatus?.docs_root || "Not checked yet"}
        </Typography>
        <TextField
          select
          size="small"
          label="Project"
          value={effectiveProjectId}
          onChange={(event) => setLocalProjectId(event.target.value)}
          fullWidth
          helperText={effectiveProjectError || (effectiveProjectsLoading ? "Loading projects..." : "Documents are scoped to the selected project.")}
          disabled={usingExternalProjectControl}
          sx={{ display: usingExternalProjectControl ? "none" : undefined }}
        >
          {effectiveProjects.map((project) => (
            <MenuItem key={project.project_id} value={project.project_id}>
              {project.label || project.project_id}
            </MenuItem>
          ))}
        </TextField>
        <FormControl size="small" fullWidth>
          <InputLabel id="document-module-filter-label">Module scope</InputLabel>
          <Select
            labelId="document-module-filter-label"
            multiple
            value={normalizedModuleFilter}
            onChange={handleModuleFilterChange}
            input={<OutlinedInput label="Module scope" />}
            renderValue={(selected) => {
              const values = Array.isArray(selected) ? selected : [selected];
              if (values.includes("__ALL_DOCUMENTS__")) return "All documents";
              if (values.length === 1 && values[0] === "__NO_MODULE__") return "Non-module documents";
              return values.join(", ");
            }}
          >
            {modulePickerOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Checkbox checked={normalizedModuleFilter.includes(option.value)} />
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tabs
          value={listMode}
          onChange={(_, value) => setListMode(value)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ minHeight: 36 }}
        >
          <Tab value="explorer" label="Explorer" sx={{ minHeight: 36 }} />
          <Tab value="flat" label="Flat list" sx={{ minHeight: 36 }} />
          <Tab value="recent" label="Recent" sx={{ minHeight: 36 }} />
        </Tabs>
        <TextField
          size="small"
          label="Find document"
          value={documentSearch}
          onChange={(e) => setDocumentSearch(e.target.value)}
          placeholder="Search title, slug, or module"
          fullWidth
          InputProps={{
            endAdornment: documentSearch ? (
              <InputAdornment position="end">
                <Button size="small" onClick={() => setDocumentSearch("")}>Clear</Button>
              </InputAdornment>
            ) : null,
          }}
        />
        <Typography variant="caption" color="text.secondary">
          Showing {visibleDocumentCount} of {documents.length} documents
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleDownloadModuleArchive("md")}
            disabled={!filteredDocuments.length}
            fullWidth
          >
            Module .md
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleDownloadModuleArchive("pdf")}
            disabled={!filteredDocuments.length}
            fullWidth
          >
            Module PDF
          </Button>
          </Stack>
        </Stack>

      {visibleComplianceModules.length ? (
        <Paper variant="outlined" sx={{ p: 1.25, backgroundColor: 'background.default' }}>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={`Standard: ${documentStandards?.standard_version || 'v1'}`} />
              <Chip size="small" label={`Modules in scope: ${visibleComplianceModules.length}`} />
            </Stack>
            {visibleComplianceModules.map((entry) => (
              <Box key={entry.module_name}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                  {entry.module_name === '__shared__' ? 'Shared documents' : entry.module_name}
                </Typography>
                {entry.missing_labels?.length ? (
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                    {entry.missing_doc_types.slice(0, 3).map((docType) => (
                      <Button
                        key={`${entry.module_name}-${docType}`}
                        size="small"
                        variant="outlined"
                        onClick={() => handleCreateStandardDocument(entry.module_name, docType)}
                        disabled={creatingStandardDoc === `${entry.module_name}:${docType}` || entry.module_name === '__shared__'}
                      >
                        Add {(documentStandards?.document_labels || {})[docType] || docType}
                      </Button>
                    ))}
                  </Stack>
                ) : (
                  <Alert severity="success" sx={{ mt: 0.75, py: 0 }}>Standard document set covered for this scope.</Alert>
                )}
              </Box>
            ))}
          </Stack>
        </Paper>
      ) : null}
      {loading || initializing ? <CircularProgress size={22} /> : null}
      {error ? (
        <Typography color="error" variant="body2" sx={{ mb: 1 }}>
          {error}
        </Typography>
      ) : null}

      {listMode === "explorer" ? (
        <List dense sx={{ pt: 0 }}>
          {groupedDocuments.map((group) => (
            <Box key={group.groupName} sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => toggleGroup(group.groupName)}
                sx={{ borderRadius: 1, py: 0.75, px: 1, backgroundColor: "action.hover" }}
              >
                <ListItemText
                  primary={group.groupName}
                  secondary={`${group.docs.length} document${group.docs.length === 1 ? "" : "s"}`}
                  primaryTypographyProps={{ variant: "subtitle2" }}
                  secondaryTypographyProps={{ variant: "caption" }}
                />
                <Typography variant="caption" color="text.secondary">
                  {isGroupExpanded(group.groupName) ? "Hide" : "Show"}
                </Typography>
              </ListItemButton>
              <Collapse in={isGroupExpanded(group.groupName)}>
                <List dense sx={{ pt: 0.5 }}>
                  {group.docs.map((doc) => (
                    <ListItemButton
                      key={doc.slug}
                      selected={selectedSlug === doc.slug}
                      onClick={() => loadDocument(doc.slug)}
                      sx={{ borderRadius: 1, mb: 0.25, ml: 1 }}
                    >
                      <ListItemText
                        primary={doc.title}
                        secondary={doc.slug}
                        primaryTypographyProps={{ variant: "body2", noWrap: true }}
                        secondaryTypographyProps={{ variant: "caption", noWrap: true }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </Box>
          ))}
          {!loading && groupedDocuments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {documents.length ? "No documents match the selected filter or search." : "No documents stored yet."}
            </Typography>
          ) : null}
        </List>
      ) : listMode === "recent" ? (
        <List dense sx={{ pt: 0 }}>
          {recentDocuments.map((doc) => (
            <ListItemButton
              key={doc.slug}
              selected={selectedSlug === doc.slug}
              onClick={() => loadDocument(doc.slug)}
              sx={{ borderRadius: 1, mb: 0.5, px: 1 }}
            >
              <ListItemText
                primary={doc.title}
                secondary={`${(Array.isArray(doc.module_names) && doc.module_names.length) ? doc.module_names.join(", ") : "Non-module document"} • ${doc.updated_at || ""}`}
                primaryTypographyProps={{ variant: "body2", noWrap: true }}
                secondaryTypographyProps={{ variant: "caption", noWrap: true }}
              />
            </ListItemButton>
          ))}
          {!loading && recentDocuments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {documents.length ? "No documents match the selected filter or search." : "No documents stored yet."}
            </Typography>
          ) : null}
        </List>
      ) : (
        <List dense sx={{ pt: 0 }}>
          {(filteredDocuments || [])
            .filter((doc) => {
              const haystack = [doc.title || "", doc.slug || "", ...(Array.isArray(doc.module_names) ? doc.module_names : [])]
                .join(" ")
                .toLowerCase();
              return !documentSearch.trim() || haystack.includes(documentSearch.trim().toLowerCase());
            })
            .sort((a, b) => (a.title || "").localeCompare(b.title || ""))
            .map((doc) => (
              <ListItemButton
                key={doc.slug}
                selected={selectedSlug === doc.slug}
                onClick={() => loadDocument(doc.slug)}
                sx={{ borderRadius: 1, mb: 0.5, px: 1 }}
              >
                <ListItemText
                  primary={doc.title}
                  secondary={(Array.isArray(doc.module_names) && doc.module_names.length) ? doc.module_names.join(", ") : "Non-module document"}
                  primaryTypographyProps={{ variant: "body2", noWrap: true }}
                  secondaryTypographyProps={{ variant: "caption", noWrap: true }}
                />
              </ListItemButton>
            ))}
          {!loading && filteredDocuments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {documents.length ? "No documents match the selected filter or search." : "No documents stored yet."}
            </Typography>
          ) : null}
        </List>
      )}
    </Paper>
  );

  const editorPanel = (
    <Paper sx={{ flex: 1.2, minWidth: 0, p: { xs: 1.5, md: 2 }, overflow: "auto" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6">Document Editor</Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedDocumentSummary
              ? `${selectedDocumentSummary.storage_path || selectedDocumentSummary.slug}`
              : "Create or select a document to begin editing."}
          </Typography>
          {selectedDocumentSummary ? (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
              <Chip size="small" label={`${selectedDocumentSummary.version_count || 0} versions`} />
              <Chip
                size="small"
                label={
                  Array.isArray(selectedDocumentSummary.module_names) && selectedDocumentSummary.module_names.length
                    ? selectedDocumentSummary.module_names.join(", ")
                    : "Non-module document"
                }
              />
            </Stack>
          ) : null}
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap" useFlexGap>
          {hasUnsavedChanges ? <Chip size="small" color="warning" label="Unsaved changes" /> : null}
          <Button variant="outlined" onClick={handleDownloadMd} disabled={!draft.content && !draft.title} fullWidth={isMobile}>
            Download .md
          </Button>
          <Button variant="outlined" onClick={handleDownloadPdf} disabled={!draft.content && !draft.title} fullWidth={isMobile}>
            Download PDF
          </Button>
          <Button variant="contained" onClick={handleOpenSaveReview} disabled={saving || !draft.title.trim()} fullWidth={isMobile}>
            {saving ? "Saving..." : "Review & Save"}
          </Button>
        </Stack>
      </Stack>

      <Stack spacing={2}>
        <TextField
          label="Title"
          value={draft.title}
          onChange={(e) => setDraft((prev) => ({ ...prev, title: e.target.value }))}
          fullWidth
        />
        <TextField
          label="Description"
          value={draft.description}
          onChange={(e) => setDraft((prev) => ({ ...prev, description: e.target.value }))}
          fullWidth
        />

        <TextField
          select
          label="Linked modules"
          value={draft.module_names || []}
          onChange={(e) =>
            setDraft((prev) => ({
              ...prev,
              module_names: typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value,
            }))
          }
          SelectProps={{ multiple: true }}
          helperText="Assign this document to one or more modules for ZIP export/import scope."
          fullWidth
        >
          {(availableModules || []).map((moduleName) => (
            <MenuItem key={moduleName} value={moduleName}>
              {moduleName}
            </MenuItem>
          ))}
        </TextField>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Markdown Source
            </Typography>
            <TextField
              label="Content"
              value={draft.content}
              onChange={(e) => setDraft((prev) => ({ ...prev, content: e.target.value }))}
              fullWidth
              multiline
              minRows={isMobile ? 14 : 22}
              sx={{ "& .MuiInputBase-input": { fontFamily: "monospace" } }}
            />
          </Box>

          <Box>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={1}
              sx={{ mb: 1 }}
            >
              <Typography variant="subtitle2">Viewer</Typography>
              <Tabs value={previewTab} onChange={(_, value) => setPreviewTab(value)} variant="scrollable" allowScrollButtonsMobile>
                <Tab value="preview" label="Preview" />
                <Tab value="raw" label="Raw" />
              </Tabs>
            </Stack>
            <Paper
              variant="outlined"
              sx={{ p: { xs: 1.5, md: 2 }, minHeight: { xs: 280, md: 470 }, maxHeight: { xs: 360, md: 580 }, overflow: "auto" }}
            >
              {previewTab === "preview" ? (
                <MarkdownPreview content={draft.content} />
              ) : (
                <Box component="pre" sx={{ m: 0, fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {draft.content || ""}
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
      </Stack>
    </Paper>
  );

  const historyPanel = (
    <Paper sx={{ width: { xs: "100%", md: 380 }, minWidth: 0, p: { xs: 1.5, md: 2 }, overflow: "auto" }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Versions
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {selectedDocumentSummary ? `Saved history for ${selectedDocumentSummary.title}` : "Select a document to view its history."}
      </Typography>

      <List dense>
        {(draft.versions || []).map((version) => (
          <Box key={version.version_name} sx={{ mb: 1.5 }}>
            <ListItemButton onClick={() => handlePreviewVersion(version.version_name)} sx={{ borderRadius: 1 }}>
              <ListItemText
                primary={version.version_name}
                secondary={
                  <Typography variant="caption" sx={{ wordBreak: "break-word" }}>
                    {version.modified_at || ""}
                  </Typography>
                }
              />
            </ListItemButton>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ px: 2, pb: 1 }}>
              <Button size="small" onClick={() => handlePreviewVersion(version.version_name)} fullWidth={isMobile}>
                Preview
              </Button>
              <Button size="small" color="warning" onClick={() => handleRestoreVersion(version.version_name)} fullWidth={isMobile}>
                Restore
              </Button>
            </Stack>
            <Divider />
          </Box>
        ))}
        {(draft.versions || []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No version history yet.
          </Typography>
        ) : null}
      </List>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={1}
        sx={{ mt: 2, mb: 1 }}
      >
        <Typography variant="subtitle2">Version Preview</Typography>
        <Tabs value={versionPreviewTab} onChange={(_, value) => setVersionPreviewTab(value)} variant="scrollable" allowScrollButtonsMobile>
          <Tab value="preview" label="Preview" />
          <Tab value="raw" label="Raw" />
        </Tabs>
      </Stack>
      {versionLoading ? <CircularProgress size={20} /> : null}
      <Paper variant="outlined" sx={{ p: 1.5, minHeight: 180, maxHeight: 280, overflow: "auto", mb: 2 }}>
        {versionPreviewTab === "preview" ? (
          <MarkdownPreview content={versionPreview} />
        ) : (
          <Box component="pre" sx={{ m: 0, fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {versionPreview}
          </Box>
        )}
      </Paper>

      <Divider sx={{ my: 2 }} />
      <Typography variant="h6" sx={{ mb: 1 }}>
        Storage Tree
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Server folders are created automatically and shown here.
      </Typography>
      {tree.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No storage folders found yet.
        </Typography>
      ) : (
        tree.map((node) => <TreeNode key={`${node.name}-${node.path || node.relative_path || ""}`} node={node} isMobile={isMobile} onCopyPath={copyTreePath} />)
      )}
    </Paper>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, height: "100%", p: { xs: 1, md: 2 } }}>
      {isMobile ? (
        <Tabs value={mobileSection} onChange={(_, value) => setMobileSection(value)} variant="scrollable" allowScrollButtonsMobile>
          <Tab value="documents" label="Documents" />
          <Tab value="editor" label="Editor" />
          <Tab value="history" label="Versions" />
        </Tabs>
      ) : null}

      {isMobile ? (
        <Box sx={{ flex: 1, minHeight: 0 }}>
          {mobileSection === "documents" ? documentsPanel : null}
          {mobileSection === "editor" ? editorPanel : null}
          {mobileSection === "history" ? historyPanel : null}
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: 2, flex: 1, minHeight: 0 }}>
          {documentsPanel}
          {editorPanel}
          {historyPanel}
        </Box>
      )}

      <Dialog open={treeModalOpen} onClose={() => setTreeModalOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Documents Folder Tree</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
              {storageStatus?.docs_root || "Documents root has not been loaded yet."}
            </Typography>
            {copiedPath ? (
              <Alert severity="success" sx={{ py: 0.5 }}>Copied path: {copiedPath}</Alert>
            ) : null}
            {storageStatus && !storageStatus.exists ? (
              <Alert severity="warning">The configured documents root does not exist yet. Use Create Documents Folder first.</Alert>
            ) : null}
            {storageStatus?.warnings?.length ? (
              <Stack spacing={0.75}>
                {storageStatus.warnings.map((item, index) => (
                  <Alert key={`${item.path}-${index}`} severity={item.severity === "error" ? "error" : "warning"} sx={{ py: 0.5 }}>
                    <strong>{item.message}</strong> {item.path ? `— ${item.path}` : ""}
                  </Alert>
                ))}
              </Stack>
            ) : null}
            {tree.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No document folders or files found under the configured docs root.</Typography>
            ) : (
              <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 520, overflow: "auto" }}>
                {tree.map((node) => (
                  <TreeNode key={`${node.name}-${node.path || node.relative_path || ""}`} node={node} isMobile={isMobile} onCopyPath={copyTreePath} />
                ))}
              </Paper>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { loadStorageStatus(); loadTree(); }}>Refresh</Button>
          <Button onClick={() => setTreeModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reviewOpen} onClose={() => setReviewOpen(false)} maxWidth="lg" fullWidth fullScreen={isMobile}>
        <DialogTitle>Review changes before save</DialogTitle>
        <DialogContent dividers>
          {diffLoading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="body2">Building diff preview…</Typography>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`Title changed: ${draft.title !== baselineDraft.title ? "Yes" : "No"}`} />
                <Chip
                  size="small"
                  label={`Description changed: ${draft.description !== baselineDraft.description ? "Yes" : "No"}`}
                />
                <Chip size="small" color="success" label={`+${saveDiff?.added_lines || 0} lines`} />
                <Chip size="small" color="error" label={`-${saveDiff?.removed_lines || 0} lines`} />
              </Stack>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Tracked changes
                </Typography>
                <DiffPreview diff={saveDiff} />
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Updated markdown preview
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, maxHeight: 320, overflow: "auto" }}>
                  <MarkdownPreview content={draft.content} />
                </Paper>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 1.5 }, flexDirection: { xs: "column", sm: "row" }, gap: 1 }}>
          <Button onClick={() => setReviewOpen(false)} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button variant="contained" onClick={performSave} disabled={saving || diffLoading || !draft.title.trim()} fullWidth={isMobile}>
            {saving ? "Saving..." : "Save document"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
